import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LockOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Space, Tag, theme as antTheme, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DocumentsContext from '@totem/components/documents/report/documentsContext';
import useDocumentAccess from '@totem/components/documents/report/useDocumentAccess';
import { DocumentSummaryEntry } from '@totem/components/documents/types';
import {
  CONTRACT_SYNC_USER,
  daysUntilExpiry,
  expiryState,
  fileTypeLabel,
  formatFileSize,
} from '@totem/components/documents/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

const FILTER_COLUMNS = [
  'type',
  'refType',
  'region',
  'building',
  'createdAt',
  'expirationDate',
];

const Sub = ({ children }: { children: React.ReactNode }) => (
  <div>
    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
      {children}
    </Typography.Text>
  </div>
);

const NotRecorded = ({ label = 'Not recorded' }: { label?: string }) => (
  <Typography.Text type="secondary">{label}</Typography.Text>
);

const hasReference = (id?: string) => Boolean(id) && id !== EMPTY_ID;

const expiresIn = (expirationDate: string) => {
  const days = daysUntilExpiry(expirationDate);
  if (days < 0) {
    return 'expired';
  }
  return days < 365 ? `in ${days} days` : `in ${Math.round(days / 365)} yr`;
};

// The name search lives in the column header, as in the repository mockup.
const NameSearch = ({ close }: { close: () => void }) => {
  const { handleFilter, getFilterValue } = useContext(InputContext);
  const [term, setTerm] = useState<string>(getFilterValue('name') ?? '');

  const apply = (value: string) => {
    handleFilter('name', value.trim());

    close();
  };

  return (
    <div
      style={{ padding: 8, width: 240 }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Input
        autoFocus
        allowClear
        placeholder="Search document name"
        value={term}
        onChange={(evt) => setTerm(evt.target.value)}
        onPressEnter={() => apply(term)}
        style={{ marginBottom: 8 }}
      />
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button
          size="small"
          type="link"
          onClick={() => {
            setTerm('');

            apply('');
          }}
        >
          Reset
        </Button>
        <Button size="small" type="primary" onClick={() => apply(term)}>
          Search
        </Button>
      </Space>
    </div>
  );
};

const RepositoryTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { data, loading, totalRecords, onAction } =
    useContext(DocumentsContext);
  const {
    handleTableChange,
    input,
    getFilterOptions,
    getFilterValues,
    getFilterValue,
  } = useContext(InputContext);
  const { canOpen, open } = useDocumentAccess();
  const { token } = antTheme.useToken();

  // Controlled so removing a filter chip also clears the column. Keyed on the selected values so the arrays only
  // change when a selection does; a new array every render makes antd fire onChange in a loop.
  const filterValuesKey = JSON.stringify(
    FILTER_COLUMNS.map((key) => getFilterValues(key)),
  );
  const filteredValues = useMemo(() => {
    const values: string[][] = JSON.parse(filterValuesKey);
    return Object.fromEntries(
      FILTER_COLUMNS.map((key, index) => [key, values[index]]),
    );
  }, [filterValuesKey]);
  const searchTerm = getFilterValue('name') ?? '';
  const nameFilterValue = useMemo(
    () => (searchTerm ? [searchTerm] : null),
    [searchTerm],
  );

  const canViewSalesOrders =
    permissions.contractsViewer.hasPermissions(userProfile);

  const canEdit = (record: DocumentSummaryEntry) =>
    record.document.createdBy === userProfile.email ||
    permissions.documentsAdministrator.hasPermissions(userProfile, {
      regionId: record.region.id,
      buildingId: record.building.id,
      controlSystemId: record.controlSystem.id,
    });

  const columns: ColumnProps<DocumentSummaryEntry>[] = [
    {
      title: 'Name',
      key: 'name',
      sorter: true,
      filteredValue: nameFilterValue,
      filterIcon: (
        <SearchOutlined
          style={{ color: searchTerm ? token.colorPrimary : undefined }}
        />
      ),
      filterDropdown: ({ close }) => <NameSearch close={close} />,
      onCell: () => ({ style: { minWidth: 190, maxWidth: 280 } }),
      render: (_, record) => (
        <>
          {canOpen(record) ? (
            <Typography.Link strong onClick={() => open(record)}>
              {record.document.displayName}
            </Typography.Link>
          ) : (
            <Space size={4}>
              <LockOutlined title="Restricted by security group" />
              <strong>{record.document.displayName}</strong>
            </Space>
          )}
          <Sub>v{record.document.version}</Sub>
        </>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      filters: getFilterOptions('type'),
      filteredValue: filteredValues.type,
      render: (_, record) => (
        <Tag>{fileTypeLabel(record.document.fileType)}</Tag>
      ),
    },
    {
      title: 'Size',
      key: 'size',
      align: 'right',
      sorter: true,
      render: (_, record) =>
        record.document.fileType === 'Link' ? (
          <NotRecorded label="Link" />
        ) : (
          formatFileSize(record.document.size)
        ),
    },
    {
      title: 'Linked to',
      key: 'refType',
      sorter: true,
      filters: getFilterOptions('refType'),
      filteredValue: filteredValues.refType,
      filterSearch: true,
      render: (_, record) => {
        const { objectType, objectId, objectDisplay } = record.document;
        if (!objectDisplay || !objectType || objectType === 'None') {
          return <NotRecorded label="Organization only" />;
        }
        const target =
          {
            Device: `/dashboard/devices/${objectId}`,
            'Maintenance Window': `/dashboard/maintenanceWindows/${objectId}`,
            Ticket: `/dashboard/ticket/${objectId}`,
            // Sales order pages are keyed by order number and need the Sales Orders permission.
            ...(canViewSalesOrders && {
              'Sales Order': `/dashboard/salesOrder/${encodeURIComponent(objectDisplay)}`,
            }),
          }[objectType] ?? null;
        return (
          <>
            {target ? <Link to={target}>{objectDisplay}</Link> : objectDisplay}
            <Sub>{objectType}</Sub>
          </>
        );
      },
    },
    {
      title: 'Region',
      key: 'region',
      sorter: true,
      filters: getFilterOptions('region'),
      filteredValue: filteredValues.region,
      filterSearch: true,
      render: (_, record) => record.region.name || <NotRecorded />,
    },
    {
      title: 'Building',
      key: 'building',
      sorter: true,
      filters: getFilterOptions('building'),
      filteredValue: filteredValues.building,
      filterSearch: true,
      render: (_, record) => {
        if (!record.building.name) {
          return <NotRecorded />;
        }
        return hasReference(record.building.id) ? (
          <Link to={`/dashboard/buildings/${record.building.id}`}>
            <strong>{record.building.name}</strong>
          </Link>
        ) : (
          <strong>{record.building.name}</strong>
        );
      },
    },
    {
      title: 'Added',
      key: 'createdAt',
      sorter: true,
      filters: getFilterOptions('createdAt'),
      filteredValue: filteredValues.createdAt,
      filterSearch: true,
      render: (_, record) => {
        const { createdAt, createdBy } = record.document;
        const who =
          createdBy === CONTRACT_SYNC_USER
            ? 'Contract sync'
            : createdBy.split('@')[0];
        return (
          <>
            {dayjs(createdAt).format('MMM D, YYYY')}
            <Sub>
              <span title={createdBy}>{who}</span> ·{' '}
              {dayjs(createdAt).format('h:mm A')}
            </Sub>
          </>
        );
      },
    },
    {
      title: 'Expires',
      key: 'expirationDate',
      sorter: true,
      filters: getFilterOptions('expirationDate'),
      filteredValue: filteredValues.expirationDate,
      render: (_, record) => {
        const { expirationDate } = record.document;
        const state = expiryState(expirationDate);
        return (
          <>
            {dayjs(expirationDate).format('MMM D, YYYY')}
            <Sub>
              {expiresIn(expirationDate)}
              {state !== 'Later' && (
                <Tag
                  color={state === 'Expired' ? 'red' : 'gold'}
                  style={{ marginLeft: 6 }}
                >
                  {state}
                </Tag>
              )}
            </Sub>
          </>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) =>
        canEdit(record) && (
          <Space size={12}>
            <Typography.Link onClick={() => onAction('edit', record)}>
              Edit
            </Typography.Link>
            <Typography.Link
              type="secondary"
              onClick={() => onAction('hard-delete', record)}
            >
              Delete
            </Typography.Link>
          </Space>
        ),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(data) ? data : []}
      loading={loading}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'No documents match these filters.' }}
      onChange={(pagination, filters, sorter, extra) => {
        // The name search is a single value set from its own dropdown; the multi-value column filters go through
        // the shared handler.
        const columnFilters = { ...filters };
        delete columnFilters.name;

        handleTableChange(pagination, columnFilters, sorter, extra);
      }}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.document.id}
    />
  );
};

export default RepositoryTable;
